import { LogLevel } from "loglayer";
import { LoggerlessTransport, LoggerlessTransportConfig, LogLayerTransportParams } from "@loglayer/transport";

import { prefixToContext } from "./prefix-context.plugin";

/**
 * LogLayer transport printing the logs in the console, the message prefix (`log.withPrefix("[sync]")`)
 * displayed as a colored badge: `[sync] Publishing changes { archipad.patch.id: … }`
 *
 * Note: the console shows this transport as the source location, not the code that logged.
 */
export class ConsoleBadgeTransport extends LoggerlessTransport {
    public constructor(config: LoggerlessTransportConfig = {}) {
        super(config);
    }

    public shipToLogger({ logLevel, messages, data, hasData, prefix }: LogLayerTransportParams): unknown[] {
        const method = toConsoleMethod(logLevel);
        const attributes = Object.fromEntries(
            // `log.context` is already displayed by the badge
            Object.entries((hasData && data) || {}).filter(
                ([key, value]) => key !== "log.context" && value !== undefined,
            ),
        );
        const message = messages.join(" ");
        const args = Object.keys(attributes).length > 0 ? [attributes] : [];

        const context = prefix ? prefixToContext(prefix) : undefined;
        if (!prefix || !context || !message.startsWith(prefix)) {
            console[method](message, ...args);
        } else {
            const messageWithoutPrefix = message.slice(prefix.length).trimStart();
            console[method]("%c%s%c %s", consoleBadgeStyle(context), context, "", messageWithoutPrefix, ...args);
        }
        return messages;
    }
}

function toConsoleMethod(logLevel: LogLayerTransportParams["logLevel"]): "debug" | "info" | "warn" | "error" {
    switch (logLevel) {
        case LogLevel.trace:
        case LogLevel.debug:
            return "debug";
        case LogLevel.warn:
            return "warn";
        case LogLevel.error:
        case LogLevel.fatal:
            return "error";
        default:
            return "info";
    }
}

/**
 * CSS of a console badge (`console.log("%c%s", style, text)`), with a stable color per text.
 */
export function consoleBadgeStyle(text: string): string {
    let hash = 0;
    for (const char of text) {
        hash = (hash * 31 + char.charCodeAt(0)) | 0;
    }
    return [
        `background: hsl(${Math.abs(hash) % 360}, 55%, 42%)`,
        "border-radius: 0.5em",
        "color: white",
        "font-weight: bold",
        "padding: 2px 0.5em",
    ].join(";");
}
